/**
 * SetupWizard (now: PostLinkInfo)
 * Informational panel shown after linking a bank account via Plaid.
 * Explains what was linked, how detection works, and what to do next.
 */

import { X, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';

interface SetupWizardProps {
  onClose: () => void;
}

export function SetupWizard({ onClose }: SetupWizardProps) {
  return (
    <section
      className="rounded-lg border-2 border-primary/30 bg-primary/5 p-6"
      data-testid="setup-wizard"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-success shrink-0 mt-0.5" aria-hidden="true" />
          <h2 className="text-base font-semibold text-foreground m-0">Bank account linked</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground shrink-0"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-4 text-sm text-muted-foreground">
        <p className="m-0">
          Resida will scan this account for incoming deposits and try to match them to your tenants
          based on the payment description — typically the sender&apos;s name for Zelle, or the
          memo for bank transfers. The more payments it sees, the more accurate matching becomes.
          You can confirm, correct, or manually assign any uncertain matches from the External
          Payments page.
        </p>

        <div>
          <p className="font-medium text-foreground mb-1">Late fees</p>
          <p className="m-0">
            For leases using external payment detection, late fees are not charged automatically.
            When a payment hasn&apos;t been detected by the end of the grace period, you&apos;ll
            see a pending late fee for review — you can approve or dismiss it.
          </p>
        </div>

        <div>
          <p className="font-medium text-foreground mb-1">Next steps</p>
          <ul className="list-disc list-inside space-y-1 m-0">
            <li>
              To use external payment detection on a lease, open the lease and set the payment
              method to <span className="font-medium text-foreground">External</span> in Payment
              Settings.
            </li>
            <li>
              Let your tenants know to use their full legal name when paying via Zelle — this helps
              the system recognize their payments.
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button onClick={onClose} data-testid="setup-wizard-done-button">
          Got it
        </Button>
      </div>
    </section>
  );
}
